import { Link } from "react-router-dom";
import { XIcon } from "lucide-react";
import { cn } from "@/lib/utils";

// Linear-style label pill: colored dot + text in a rounded border.
// Used both for read-only/filter display (in lists) and the editable tag editor.
const DOT_COLORS = [
  "bg-purple-500",
  "bg-blue-500",
  "bg-green-500",
  "bg-amber-500",
  "bg-pink-500",
  "bg-cyan-500",
  "bg-red-500",
  "bg-indigo-500",
];

// Stable color per tag name (a label keeps the same dot color everywhere).
export const tagDotColor = (t: string) => {
  let h = 0;
  for (let i = 0; i < t.length; i++) h = (h * 31 + t.charCodeAt(i)) >>> 0;
  return DOT_COLORS[h % DOT_COLORS.length];
};

// The colored dot alone (no pill). Use where a tag color marker is needed
// outside a TagPill (e.g. sidebar pins, tag detail header).
export const TagDot = ({
  tag,
  color,
  className,
}: {
  tag: string;
  color?: string;
  className?: string;
}) => (
  <span className={cn("h-2 w-2 rounded-full", color ?? tagDotColor(tag), className)} />
);

export function TagPill({
  tag,
  color,
  to,
  onRemove,
  className,
}: {
  tag: string;
  color?: string;
  to?: string;
  onRemove?: () => void;
  className?: string;
}) {
  const cls = cn(
    "group inline-flex items-center gap-1.5 rounded-full border bg-card py-0.5 text-xs font-medium text-foreground",
    onRemove ? "pl-2 pr-1.5" : "pl-2 pr-2.5",
    to && "transition-colors hover:bg-accent",
    className
  );
  const inner = (
    <>
      <TagDot tag={tag} color={color} />
      {tag}
      {onRemove && (
        <button
          type="button"
          title="Remove label"
          onClick={(e) => {
            e.preventDefault();
            onRemove();
          }}
          className="text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"
        >
          <XIcon className="h-3 w-3" />
        </button>
      )}
    </>
  );

  if (to) {
    return (
      <Link
        to={to}
        title={`${tag} — view PRDs`}
        className={cls}
        // Pills often sit inside clickable row cards — keep the pill's own
        // navigation instead of letting the row's onClick win.
        onClick={(e) => e.stopPropagation()}
      >
        {inner}
      </Link>
    );
  }
  return <span className={cls}>{inner}</span>;
}
